import { RefObject, useEffect } from 'react'

import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'

// iOS Safari ignores `overflow: hidden` on body; pinning with `position: fixed` + restoring scrollY is the only reliable lock. Ref-counted so nested modals don't fight each other.
// Exception: on iOS Safari, `position: fixed` resets the browser's internal scroll-position tracking,
// causing the native Smart App Banner to reappear when a drawer opens. Use overflow:hidden on <html> instead (reliable on iOS 16+).
let lockCount = 0
let savedScrollY = 0
let savedDocHeight = 0
let savedPathname = ''
let savedBodyStyles: {
  position: string
  top: string
  left: string
  right: string
  width: string
} | null = null

// Evaluated once at module load (server-side navigator is undefined → false, which is correct).
const isIOSSafari =
  typeof navigator !== 'undefined' &&
  /iP(hone|od|ad)/.test(navigator.userAgent) &&
  !/CriOS|FxiOS/.test(navigator.userAgent) &&
  !getIsNativeViewClient()

function lockBody() {
  const body = document.body
  const isFirstLock = lockCount === 0

  if (isFirstLock) {
    savedScrollY = window.scrollY
    savedDocHeight = document.documentElement.scrollHeight
    savedPathname = window.location.pathname
    savedBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    }
  }

  // Capture page geometry before changing root/body styles. Reading `scrollHeight` after the class
  // mutation forces the browser to synchronously recalculate styles for the whole document.
  body.classList.add('overflowHidden')
  body.classList.remove('overflowUnset')

  if (isFirstLock) {
    if (isIOSSafari) {
      // position:fixed on body resets Safari's scroll-position tracking → native Smart App Banner
      // reappears (Safari interprets it as "scrolled to top"). Locking overflow on the root
      // element avoids that side-effect while still preventing body scroll on iOS 16+.
      document.documentElement.style.overflow = 'hidden' // eslint-disable-line i18next/no-literal-string
      // Legacy pages set html/body height:100%, so hiding root overflow collapses the scroll
      // range and the browser clamps scrollY to 0 — the page would visibly jump to the top
      // behind the sheet. Hold the content in place with a measured relative offset;
      // position:relative (never fixed) keeps Safari's scroll tracking intact.
      const clampedBy = savedScrollY - window.scrollY
      if (clampedBy > 0) {
        body.style.position = 'relative' // eslint-disable-line i18next/no-literal-string
        body.style.top = `-${clampedBy}px`
      }
    } else {
      body.style.position = 'fixed'
      body.style.top = `-${savedScrollY}px`
      body.style.left = '0'
      body.style.right = '0'
      body.style.width = '100%'
    }
  }
  lockCount += 1
}

function unlockBody() {
  const body = document.body
  body.classList.add('overflowUnset')
  body.classList.remove('overflowHidden')

  lockCount = Math.max(0, lockCount - 1)
  if (lockCount > 0) return

  if (savedBodyStyles) {
    if (isIOSSafari) {
      document.documentElement.style.overflow = ''
      body.style.position = savedBodyStyles.position
      body.style.top = savedBodyStyles.top
    } else {
      body.style.position = savedBodyStyles.position
      body.style.top = savedBodyStyles.top
      body.style.left = savedBodyStyles.left
      body.style.right = savedBodyStyles.right
      body.style.width = savedBodyStyles.width
    }
    // Both locks lose the live scroll position — the body pin reports scrollY 0 while pinned, and
    // the root overflow:hidden branch clamps scrollY to 0 outright (legacy pages set html/body
    // height:100%, so hiding root overflow collapses the scroll range) — so an in-place unlock
    // restores the pre-lock position. Across a navigation (the QATC "view full product" flow
    // unlocks after the route change), restoring would scroll the fresh page to the previous
    // page's offset; instead reset the legacy header's persistent scrolling-down flag, which
    // otherwise strands the navbar hidden on the new page.
    if (window.location.pathname === savedPathname) {
      window.scrollTo(0, savedScrollY)
    } else {
      siteStateManager.setIsScrollingDown(false)
    }
    savedBodyStyles = null
  }
}

/**
 * The user's real scroll position, even while a modal holds the body lock.
 *
 * The lock pins <body> with `position: fixed; top: -<savedScrollY>px`, so the browser reports
 * `window.scrollY === 0` for as long as it is on — the true position lives only in `savedScrollY`.
 * Anything that must read the position while a modal is open has to come through here. PDP scroll
 * restoration is the case that bit us: it saves on `routeChangeStart`, and the QATC "view full
 * product details" link only *schedules* the unlock (a React effect cleanup), so the lock is still
 * on when the save runs — a raw `window.scrollY` records 0 and Back then lands at the top.
 * [STRFRNT-15288]
 */
export const getUnlockedScrollY = (): number => (lockCount > 0 ? savedScrollY : window.scrollY)

/**
 * The document's real height, even while a modal holds the body lock.
 *
 * Same trap as `getUnlockedScrollY`, one property over: `position: fixed` takes <body> out of flow
 * and `overflow: hidden` clips it, so `document.documentElement.scrollHeight` collapses to exactly the
 * viewport while a modal is open — measured on pre-integ, 10088 -> 900. Anything deciding "was the
 * reader at the end of the page" during a locked navigation has to come through here, or every modal
 * exit looks like it happened at the bottom. [STRFRNT-15288]
 */
export const getUnlockedDocHeight = (): number =>
  lockCount > 0 ? savedDocHeight : document.documentElement.scrollHeight

const useLockBodyScroll = (freeze: boolean, contentRef?: RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!freeze) return

    lockBody()

    let preventScrollOutsideModal: ((e: TouchEvent) => void) | undefined
    if (contentRef) {
      preventScrollOutsideModal = (e: TouchEvent) => {
        if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
          e.preventDefault()
        }
      }
      document.addEventListener('touchmove', preventScrollOutsideModal, { passive: false })
    }

    return () => {
      unlockBody()
      if (preventScrollOutsideModal) {
        document.removeEventListener('touchmove', preventScrollOutsideModal)
      }
    }
  }, [freeze, contentRef])
}

export default useLockBodyScroll
